import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Minus, Plus, Trash2, Palette, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { DesignStudioModal } from './DesignStudioModal';
import { ADD_ONS, PREDEFINED_EXTRAS } from '../constants/bookingCatalog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Toggle (on/off) items vs quantity items — mirrors the public booking page.
export const TOGGLE_ITEM_IDS = new Set(['diy_cake_custom', 'extra_30_min']);
export const PRINT_GARMENT_MAP = { print_cap: 'cap', print_tshirt: 'tshirt', print_mug: 'mug', byo_print: 'tshirt' };

export const flattenCatalog = (catalog) => (catalog?.categories || []).flatMap((c) => c.items || []);

const catalogIndex = (catalog) => {
  const items = flattenCatalog(catalog);
  return Object.fromEntries(items.map((it) => [it.item_id, it]));
};

// Sum of add-ons (toggles + quantity items) priced from the catalog (legacy fallback).
export const computeAddOnsTotal = (catalog, selectedAddOns = {}, extrasQty = {}) => {
  const byId = catalogIndex(catalog);
  let total = 0;
  Object.entries(selectedAddOns).forEach(([id, on]) => {
    if (!on) return;
    const it = byId[id] || ADD_ONS.find((a) => a.id === id);
    if (it) total += Number(it.price) || 0;
  });
  Object.entries(extrasQty).forEach(([id, q]) => {
    const qty = Number(q) || 0;
    if (qty <= 0 || TOGGLE_ITEM_IDS.has(id)) return;
    const it = byId[id] || PREDEFINED_EXTRAS.find((p) => p.id === id);
    if (it) total += qty * (Number(it.price) || 0);
  });
  return Math.round(total * 100) / 100;
};

export const computeCustomExtrasTotal = (customExtras = []) =>
  Math.round(customExtras.reduce((s, e) => {
    const p = Number(e.unit_price) || 0; const q = Number(e.quantity) || 0;
    return s + (p > 0 && q > 0 && (e.name || '').trim() ? p * q : 0);
  }, 0) * 100) / 100;

// Build the flat `add_ons` list the backend expects.
export const buildAddOnsPayload = (catalog, selectedAddOns = {}, extrasQty = {}) => {
  const byId = catalogIndex(catalog);
  const out = [];
  Object.entries(selectedAddOns).forEach(([id, on]) => {
    if (!on) return;
    const it = byId[id] || ADD_ONS.find((a) => a.id === id);
    if (it) out.push({ id, name: it.name, quantity: 1, price: Number(it.price) || 0 });
  });
  Object.entries(extrasQty).forEach(([id, q]) => {
    const qty = Number(q) || 0;
    if (qty <= 0 || TOGGLE_ITEM_IDS.has(id)) return;
    const it = byId[id] || PREDEFINED_EXTRAS.find((p) => p.id === id);
    if (it) out.push({ id, name: it.name, quantity: qty, price: Number(it.price) || 0 });
  });
  return out;
};

export const buildCustomChargesPayload = (customExtras = []) =>
  customExtras
    .filter((e) => (e.name || '').trim() && Number(e.unit_price) > 0 && Number(e.quantity) > 0)
    .map((e) => ({
      ...(e.charge_id && !String(e.charge_id).startsWith('tmp_') ? { charge_id: e.charge_id } : {}),
      name: String(e.name).trim().slice(0, 50),
      unit_price: Number(e.unit_price),
      quantity: Number(e.quantity),
    }));

export const buildDesignsPayload = (designs = []) =>
  designs.map((d) => ({
    garment: d.garment,
    template_id: d.template_id || null,
    image_data_url: d.image_data_url || null,
    label: d.label || '',
  }));

// Seed component state from an existing booking's saved selections.
export const seedFromBooking = (booking) => {
  const selectedAddOns = {}; const extrasQty = {};
  (booking?.add_ons || []).forEach((a) => {
    if (TOGGLE_ITEM_IDS.has(a.id)) selectedAddOns[a.id] = true;
    else extrasQty[a.id] = Number(a.quantity) || 0;
  });
  const customExtras = (booking?.custom_charges || []).map((c, i) => ({
    id: c.charge_id || `seed_${i}_${Math.random().toString(36).slice(2, 7)}`,
    charge_id: c.charge_id,
    name: c.name || c.description || '',
    unit_price: c.unit_price ?? 0,
    quantity: c.quantity ?? 1,
  }));
  const designs = (booking?.designs || []).map((d, i) => ({
    id: `seed_design_${i}_${Math.random().toString(36).slice(2, 7)}`,
    garment: d.garment || 'tshirt',
    template_id: d.template_id || null,
    image_data_url: d.image_data_url || null,
    label: d.label || '',
  }));
  return { selectedAddOns, extrasQty, customExtras, designs };
};

// Compact add-on card — identical behaviour to the public booking page.
const AddonItemCard = ({ item, isToggle, toggleValue, qtyValue, onToggle, onQtyChange, disabled }) => {
  const selected = isToggle ? !!toggleValue : qtyValue > 0;
  const max = Number(item.max_qty) || 50;
  const priceLabel = item.unit_label === 'flat' ? 'flat' : item.unit_label || 'each';
  return (
    <div
      className={`rounded-xl border-2 transition-all p-2 flex flex-col gap-1.5 ${
        selected ? 'border-[#FF3B6B] bg-[#FFEDF3] shadow-sm' : 'border-[#E8E5DF] bg-white hover:border-[#FF6B6B]/40'
      } ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
      data-testid={`addon-card-${item.item_id}`}
    >
      <div className="w-full aspect-square rounded-lg overflow-hidden bg-[#FAF8F3] flex items-center justify-center">
        {item.image_url ? (
          <img src={`${API_URL}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (<span className="text-3xl">🎉</span>)}
      </div>
      <div className="flex-1 min-h-[28px]">
        <p className="text-xs font-semibold text-[#1E1E24] leading-tight line-clamp-2">{item.name}</p>
      </div>
      <div className="flex items-center justify-between gap-1">
        <p className="text-xs font-bold text-[#FF3B6B] tabular-nums leading-none">
          ${Number(item.price || 0).toFixed(2)}
          <span className="block text-[9px] text-[#6B6B7A] font-normal">{priceLabel}</span>
        </p>
        {isToggle ? (
          <button type="button" onClick={() => onToggle(!toggleValue)} disabled={disabled}
            className={`px-2 h-7 rounded-full text-[11px] font-semibold transition-colors whitespace-nowrap ${
              toggleValue ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]'}`}
            data-testid={`addon-toggle-${item.item_id}`}>
            {toggleValue ? '✓' : '+ Add'}
          </button>
        ) : (
          <div className="flex items-center gap-0.5 bg-white rounded-full border border-[#E8E5DF] px-0.5 py-0.5">
            <button type="button" onClick={() => onQtyChange(Math.max(0, qtyValue - 1))} disabled={disabled || qtyValue <= 0}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] hover:bg-[#FFEDF3]"
              data-testid={`addon-minus-${item.item_id}`}><Minus className="w-3 h-3" /></button>
            <span className="text-xs font-bold tabular-nums w-5 text-center" data-testid={`addon-qty-${item.item_id}`}>{qtyValue}</span>
            <button type="button" onClick={() => onQtyChange(Math.min(max, qtyValue + 1))} disabled={disabled || qtyValue >= max}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] hover:bg-[#FFEDF3]"
              data-testid={`addon-plus-${item.item_id}`}><Plus className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Shared "Add Extras (optional)" menu — the single menu used on the public
 * booking page AND both Edit Booking dialogs. Controlled component: the parent
 * owns the selection state so it can compute totals and build the save payload
 * (via the exported helpers above).
 */
export const AddExtrasMenu = ({
  catalog: catalogProp,
  selectedAddOns, setSelectedAddOns,
  extrasQty, setExtrasQty,
  customExtras, setCustomExtras,
  designs, setDesigns,
  childName = '',
  disabled = false,
}) => {
  const [catalogInt, setCatalogInt] = useState(null);
  const [loading, setLoading] = useState(!catalogProp);
  const [activeCat, setActiveCat] = useState(null);
  const [designOpen, setDesignOpen] = useState(false);
  const [designGarment, setDesignGarment] = useState('tshirt');
  const [editingDesign, setEditingDesign] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [draft, setDraft] = useState({ name: '', unit_price: '', quantity: 1 });

  const catalog = catalogProp || catalogInt;

  const fetchCatalog = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/addons/catalog`);
      setCatalogInt(r.data || { categories: [] });
    } catch { toast.error('Failed to load extras'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!catalogProp) fetchCatalog(); else setLoading(false); }, [catalogProp, fetchCatalog]);

  const cats = (catalog?.categories || []).filter((c) => (c.items || []).length > 0);
  useEffect(() => { if (!activeCat && cats.length) setActiveCat(cats[0].category_id); }, [cats, activeCat]);

  const openDesignStudio = (garment = 'tshirt', design = null) => {
    setDesignGarment(garment);
    setEditingDesign(design);
    setDesignOpen(true);
  };
  const onDesignSaved = (design) => {
    if (editingDesign) {
      setDesigns((p) => p.map((d) => (d.id === editingDesign.id ? { ...design, id: editingDesign.id } : d)));
      toast.success('Design updated');
    } else {
      setDesigns((p) => [...p, { ...design, id: `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }]);
      toast.success('Design saved');
    }
    setEditingDesign(null);
  };
  const removeDesign = (id) => setDesigns((p) => p.filter((d) => d.id !== id));

  const printAddons = Object.keys(PRINT_GARMENT_MAP).filter((id) => (extrasQty[id] || 0) > 0);
  const canDesign = printAddons.length > 0;

  if (loading) {
    return <div className="py-10 flex items-center justify-center gap-2 text-[#6B6B7A] text-sm" data-testid="extras-loading"><Loader2 className="w-5 h-5 animate-spin text-[#FF3B6B]" /> Loading add-ons…</div>;
  }

  const active = cats.find((c) => c.category_id === activeCat) || cats[0];

  return (
    <div data-testid="add-extras-menu">
      {cats.length > 0 && active && (
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-thin" data-testid="addon-cat-tabs">
            {cats.map((cat) => {
              const catQty = (cat.items || []).reduce((sum, it) => (TOGGLE_ITEM_IDS.has(it.item_id) ? sum + (selectedAddOns[it.item_id] ? 1 : 0) : sum + (extrasQty[it.item_id] || 0)), 0);
              const isActive = active.category_id === cat.category_id;
              return (
                <button key={cat.category_id} type="button" onClick={() => setActiveCat(cat.category_id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium snap-start transition-colors flex-shrink-0 ${isActive ? 'bg-[#FF3B6B] text-white shadow-sm' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]'}`}
                  data-testid={`addon-cat-tab-${cat.category_id}`}>
                  <span>{cat.icon}</span>{cat.name}
                  {catQty > 0 && <span className={`rounded-full text-[10px] px-1.5 py-0.5 leading-none ${isActive ? 'bg-white/25' : 'bg-[#FF3B6B] text-white'}`}>{catQty}</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5" data-testid={`addon-grid-${active.category_id}`}>
            {active.items.map((it) => (
              <AddonItemCard key={it.item_id} item={it} isToggle={TOGGLE_ITEM_IDS.has(it.item_id)}
                toggleValue={!!selectedAddOns[it.item_id]} qtyValue={extrasQty[it.item_id] || 0}
                disabled={disabled}
                onToggle={(v) => setSelectedAddOns((s) => ({ ...s, [it.item_id]: v }))}
                onQtyChange={(q) => setExtrasQty((s) => ({ ...s, [it.item_id]: q }))} />
            ))}
          </div>
        </div>
      )}

      {/* Design Studio */}
      <div className="mb-4 rounded-2xl p-4 border-2 border-dashed" style={{ borderColor: canDesign ? '#FF3B6B' : '#E8E5DF', backgroundColor: canDesign ? '#FFF0F3' : '#FFFDF8' }} data-testid="design-studio-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <Palette className="w-5 h-5 text-[#FF3B6B] mt-0.5" />
            <div>
              <h4 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Design Studio</h4>
              <p className="text-xs text-[#6B6B7A]">{canDesign ? 'Design your printed items — upload a photo or pick a template.' : 'Add Print-a-Cap, T-Shirt or Mug above to unlock.'}</p>
            </div>
          </div>
          <Button type="button" onClick={() => openDesignStudio(PRINT_GARMENT_MAP[printAddons[0]] || 'tshirt', null)} disabled={!canDesign || disabled}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white disabled:opacity-50" data-testid="open-design-studio-btn">
            <Sparkles className="w-4 h-4 mr-1.5" />{designs.length > 0 ? 'Add another' : 'Open Design Studio'}
          </Button>
        </div>
        {designs.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="saved-designs-list">
            {designs.map((d) => (
              <button key={d.id} type="button" onClick={() => !disabled && openDesignStudio(d.garment || 'tshirt', d)}
                className="relative bg-white rounded-xl border border-[#E8E5DF] p-2 flex items-center gap-2 text-left hover:border-[#FF3B6B] transition-colors"
                data-testid={`saved-design-${d.id}`} title="Click to edit this design">
                <span className="text-2xl">{d.garment === 'cap' ? '🧢' : d.garment === 'mug' ? '☕' : '👕'}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold capitalize truncate">{d.garment === 'tshirt' ? 'T-Shirt' : d.garment}</p>
                  <p className="text-[10px] text-[#6B6B7A] truncate">{d.label || (d.template_id ? d.template_id : d.image_data_url ? 'Uploaded' : 'Tap to edit')}</p>
                </div>
                <span onClick={(e) => { e.stopPropagation(); if (!disabled) removeDesign(d.id); }}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full text-[#FF3B6B] hover:bg-[#FF3B6B]/10 flex items-center justify-center"
                  data-testid={`remove-design-${d.id}`}><Trash2 className="w-3 h-3" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Custom Extras */}
      <div className="rounded-2xl bg-white border border-[#E8E5DF] p-4" data-testid="custom-extras-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>➕ Custom Extras</h3>
          {customExtras.length > 0 && <Badge className="bg-[#FF3B6B] hover:bg-[#FF3B6B] text-white rounded-full text-[10px]">{customExtras.length} added</Badge>}
        </div>
        {customExtras.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {customExtras.map((ce) => (
              <div key={ce.id} className="flex items-center gap-3 bg-[#FFEDF3] border border-[#FF3B6B] rounded-xl p-3" data-testid={`custom-extra-${ce.id}`}>
                <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{ce.name}</p><p className="text-[11px] text-[#6B6B7A]">{ce.quantity} × ${Number(ce.unit_price || 0).toFixed(2)} = <strong className="text-[#FF3B6B]">${(Number(ce.unit_price || 0) * Number(ce.quantity || 0)).toFixed(2)}</strong></p></div>
                <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={() => setCustomExtras((a) => a.filter((x) => x.id !== ce.id))} className="h-7 w-7 p-0 text-[#D4302A]" data-testid={`custom-extra-remove-${ce.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
        {!showCustomForm ? (
          <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => { setShowCustomForm(true); setDraft({ name: '', unit_price: '', quantity: 1 }); }}
            className="rounded-full border-dashed border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3]" data-testid="add-custom-extra-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Extra</Button>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_90px_auto] gap-2 items-end p-3 bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl">
            <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Item</label><Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Extra hour, host tip…" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-extra-name-input" /></div>
            <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Price ($)</label><Input type="number" step="0.01" min="0" value={draft.unit_price} onChange={(e) => setDraft((d) => ({ ...d, unit_price: e.target.value }))} className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-extra-price-input" /></div>
            <div><label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Qty</label><Input type="number" min="1" max="99" value={draft.quantity} onChange={(e) => setDraft((d) => ({ ...d, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }))} className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-extra-qty-input" /></div>
            <div className="flex gap-1">
              <Button type="button" size="sm" onClick={() => {
                const name = (draft.name || '').trim(); const price = parseFloat(draft.unit_price); const q = parseInt(draft.quantity, 10) || 1;
                if (!name) { toast.error('Item name is required'); return; }
                if (!Number.isFinite(price) || price <= 0) { toast.error('Price must be > 0'); return; }
                setCustomExtras((a) => [...a, { id: `custom_${Date.now().toString(36)}`, name, unit_price: price, quantity: q }]); setShowCustomForm(false);
              }} className="h-9 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs" data-testid="custom-extra-add-confirm">Add</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowCustomForm(false)} className="h-9 rounded-full text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <DesignStudioModal
        open={designOpen}
        onOpenChange={setDesignOpen}
        initialGarment={designGarment}
        initialDesign={editingDesign}
        childName={childName}
        onSave={onDesignSaved}
      />
    </div>
  );
};

export default AddExtrasMenu;
